import KeyFigureContextRepositoryWrapper from "../repositories/KeyFigureContextRepositoryWrapper.js";

/**
 * @class
 * Service used as a single entry point to the keyFigures.
 * It handles the communication with the back-end via repositories
 * and manages a cache.
 *
 * :warning: All the API is and should be async (using Promise).
 * :danger: This class should probably never be used as is but instead be created through the `KeyFigureServiceFactory`.
 *
 * Out of scope:
 *  - eagerly return cached data and perform background fetch à la swr
 */
export default class KeyFigureService {
	/**
	 * @param {object} spec - Object parameters
	 * @param {Cache} spec.cache - cache object to store keyFigures
	 * @param {Repository} spec.repository - repository used communicate with the back-end when it comes to keyFigures
	 */
	constructor({ cache, repository }) {
		this._cache = cache;

		this.setRepository(repository);
	}

	/**
	 * Get a KeyFigure using its id.
	 *
	 * @param {string} id - Unique identifier
	 * @param {object} [repositoryOptions] - Options to pass to the repository
	 * @return {Promise}
	 */
	get(id, repositoryOptions) {
		const cachedKeyFigure = this._cache.get(id);

		if (cachedKeyFigure) {
			return Promise.resolve(cachedKeyFigure);
		}

		return this._repository
			.get({
				filter: { id },
				...this._injectHelperInRepositoryOptions(repositoryOptions),
			})
			.then((keyFigure) => {
				this._cache.register(keyFigure);
				return keyFigure;
			});
	}

	/**
	 * Populate the whole cache by fetching all the keyFigures at once.
	 *
	 * @param {object} [repositoryOptions] - Options to pass to the repository
	 * @return {Promise} Resolves to `null` as we should not need all the keyFigures at once.
	 * Use `get` with specific keyFigure ids instead
	 */
	populateCache(repositoryOptions) {
		const cachedData = this._cache.getAll();

		this._flushCache();

		return this._repository
			.get(this._injectHelperInRepositoryOptions(repositoryOptions))
			.then((allKeyFigures) => this._cache.registerAll(allKeyFigures))
			.catch(() => this._cache.registerAll(cachedData));
	}

	/**
	 * Returned all the cached keyFigures.
	 *
	 * @return {Promise} Resolves to a collection of keyFigures
	 */
	getAllFromCache() {
		return Promise.resolve(this._cache.getAll());
	}

	/**
	 * Returned all the cached keyFigures as a map with `id` as key.
	 *
	 * @return {Promise} Resolves to a collection of keyFigures
	 */
	getAllFromCacheAsMap() {
		return Promise.resolve(this._cache.getAllAsMap());
	}

	/**
	 * Store a new keyFigure and update the cache accordingly.
	 *
	 * @param {KeyFigure} keyFigure - Model to create
	 * @param {object} [repositoryOptions] - Options to pass to the repository
	 * @return {Promise} Resolves to the newly created keyFigure
	 */
	create(keyFigure, repositoryOptions = {}) {
		return this._repository
			.save({
				model: keyFigure,
				...this._injectHelperInRepositoryOptions(repositoryOptions),
			})
			.then((savedKeyFigure) => {
				this._cache.register(savedKeyFigure);
				return savedKeyFigure;
			});
	}

	/**
	 * Update an already stored keyFigure and update the cache accordingly.
	 *
	 * @param {KeyFigure} keyFigure - Model to update
	 * @param {object} [repositoryOptions] - Options to pass to the repository
	 * @return {Promise} Resolves to the modified keyFigure
	 */
	update(keyFigure, repositoryOptions) {
		const cachedData = this._cache.get(keyFigure.getId());

		this._removeFromCache(keyFigure);

		return this._repository
			.update({
				model: keyFigure,
				filter: { id: keyFigure.getId() },
				...this._injectHelperInRepositoryOptions(repositoryOptions),
			})
			.then((savedKeyFigure) => {
				this._cache.register(savedKeyFigure);
				return savedKeyFigure;
			})
			.catch(() => {
				if (!cachedData) {
					return;
				}
				this._cache.register(cachedData);
			});
	}

	/**
	 * Remove a keyFigure. In the case where the keyFigure was an override we need to
	 * repopulate the cache with the base version of the keyFigure. In this case, the base
	 * keyFigure is returned.
	 *
	 * @param {KeyFigure} keyFigure - Model to remove
	 * @param {object} [repositoryOptions] - Options to pass to the repository
	 * @return {Promise} Resolve to `null` when removing a custom keyFigure. Otherwise resolves to the base
	 * version of the keyFigure
	 */
	remove(keyFigure, repositoryOptions) {
		return this._repository
			.remove({
				filter: { id: keyFigure.getId() },
				...this._injectHelperInRepositoryOptions(repositoryOptions),
			})
			.then(() => {
				this._removeFromCache(keyFigure);
				if (!keyFigure.isKnown()) {
					return null;
				}

				return this.get(keyFigure.getId());
			});
	}

	setRepository(repository) {
		this._repository = new KeyFigureContextRepositoryWrapper({
			repository,
			keyFigureService: this,
		});
	}

	/**
	 * Return a string containing the URL of the key figure resource
	 * whose ID is passed as parameter.
	 */
	getDownloadUrl(keyFigureId) {
		return this._repository.buildUrl({ filter: { id: keyFigureId } });
	}

	_removeFromCache(keyFigure) {
		this._cache.unregister(keyFigure);
	}

	_flushCache() {
		this._cache.flush();
	}

	_injectHelperInRepositoryOptions(repositoryOptions = {}) {
		const result = {
			context: {},
			...repositoryOptions,
		};

		// We wrap a function in a function to by-pass repositoryContext.setExtraProperties
		// `getResult` mechanism that executes functions at build time where here, we would like
		// to pass a function as extra property.
		result.context.getKeyFigureFromCache = () => (id) =>
			this._cache.get(id);

		return result;
	}
}
